import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  FaHome, 
  FaHdd,
  FaFolder, 
  FaFolderOpen,
  FaChevronDown,
  FaChevronRight,
  FaPlus,
  FaChartBar
} from 'react-icons/fa';
import { fileService } from '../services/api';
import TempFoldersList from './TempFoldersList';

// Componente FolderItem para mostrar una carpeta con subcarpetas
const FolderItem = ({ folder, path, onFolderOpen, currentPath }) => {
  const [expanded, setExpanded] = useState(false);
  const [subfolders, setSubfolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSubfolders, setHasSubfolders] = useState(null); // null = no verificado, true/false = verificado
  
  // Asegurarse de que la ruta tenga el formato correcto
  const folderPath = useMemo(() => {
    if (!path) {
      return `${folder}/`;
    } 
    return path.endsWith('/') ? `${path}${folder}/` : `${path}/${folder}/`;
  }, [folder, path]);
  
  // Definir loadSubfolders antes de usarla en useEffect
  const loadSubfolders = useCallback(async () => {
    setLoading(true);
    try {
      console.log(`📂 Cargando subcarpetas de: "${folderPath}"`);
      // Cargar archivos específicos de esta carpeta
      const result = await fileService.getFiles(folderPath);
      const files = result.data || [];
      const subfolderSet = new Set();
      
      files.forEach(file => {
        const filePath = file.Key || file.name || '';
        
        // Si es una carpeta real (termina en /)
        if (filePath.endsWith('/') && filePath.startsWith(folderPath) && filePath !== folderPath) {
          const relativePath = filePath.substring(folderPath.length);
          const subfolderName = relativePath.split('/')[0];
          if (subfolderName && subfolderName.trim()) {
            subfolderSet.add(subfolderName);
          }
        }
        // Si es un archivo dentro de una subcarpeta (contiene más slashes)
        else if (filePath.startsWith(folderPath) && filePath !== folderPath) {
          const relativePath = filePath.substring(folderPath.length);
          if (relativePath.includes('/')) {
            const subfolderName = relativePath.split('/')[0];
            if (subfolderName && subfolderName.trim()) {
              subfolderSet.add(subfolderName);
            }
          }
        }
      });
      
      const subfoldersArray = Array.from(subfolderSet).sort();
      console.log(`✅ Subcarpetas cargadas para "${folder}": [${subfoldersArray.join(', ')}]`);
      setSubfolders(subfoldersArray);
      
      // Actualizar hasSubfolders basado en lo encontrado
      if (hasSubfolders === null) {
        setHasSubfolders(subfoldersArray.length > 0);
      }
    } catch (error) {
      console.error(`❌ Error cargando subcarpetas para "${folder}":`, error);
      setSubfolders([]);
      setHasSubfolders(false);
    } finally {
      setLoading(false);
    }
  }, [folderPath, hasSubfolders, folder]);
  
  // Verificar si tiene subcarpetas de forma más eficiente
  const checkForSubfolders = useCallback(async () => {
    if (hasSubfolders !== null) return; // Ya verificado
    
    try {
      console.log(`🔍 Verificando subcarpetas para: "${folderPath}"`);
      // Cargar archivos específicos de esta carpeta
      const result = await fileService.getFiles(folderPath);
      const files = result.data || [];
      
      // Verificar si hay subcarpetas reales o virtuales
      const subfolderSet = new Set();
      
      files.forEach(file => {
        const filePath = file.Key || file.name || '';
        
        // Si es una carpeta real (termina en /)
        if (filePath.endsWith('/') && filePath.startsWith(folderPath) && filePath !== folderPath) {
          const relativePath = filePath.substring(folderPath.length);
          const subfolderName = relativePath.split('/')[0];
          if (subfolderName) {
            subfolderSet.add(subfolderName);
          }
        }
        // Si es un archivo dentro de una subcarpeta (contiene más slashes)
        else if (filePath.startsWith(folderPath) && filePath !== folderPath) {
          const relativePath = filePath.substring(folderPath.length);
          if (relativePath.includes('/')) {
            const subfolderName = relativePath.split('/')[0];
            if (subfolderName) {
              subfolderSet.add(subfolderName);
            }
          }
        }
      });
      
      const hasSubfoldersFound = subfolderSet.size > 0;
      console.log(`✅ Subcarpetas encontradas para "${folder}": ${hasSubfoldersFound} (${subfolderSet.size} carpetas)`);
      setHasSubfolders(hasSubfoldersFound);
    } catch (error) {
      console.error(`❌ Error verificando subcarpetas para "${folder}":`, error);
      setHasSubfolders(false);
    }
  }, [folderPath, hasSubfolders, folder]);
  
  // Verificar subcarpetas al hacer hover o cuando sea necesario
  const handleMouseEnter = useCallback(() => {
    if (hasSubfolders === null) {
      checkForSubfolders();
    }
  }, [checkForSubfolders, hasSubfolders]);
  
  // Verificar subcarpetas automáticamente para carpetas de primer nivel
  useEffect(() => {
    // Solo verificar automáticamente para carpetas de primer nivel (sin path)
    if (!path || path === '') {
      checkForSubfolders();
    }
  }, [path, checkForSubfolders]);
  
  // Cargar subcarpetas cuando se expande por primera vez
  useEffect(() => {
    if (expanded && subfolders.length === 0) {
      loadSubfolders();
    }
  }, [expanded, loadSubfolders, subfolders.length]);
  
  return (
    <div>
      <div className="flex items-center" onMouseEnter={handleMouseEnter}>
        {/* Mostrar chevron solo si tenemos confirmación de subcarpetas */}
        {hasSubfolders === true ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-1 text-gray-400 hover:text-white transition-colors flex-shrink-0"
            title={expanded ? "Contraer" : "Expandir"}
          >
            {expanded ? 
              <FaChevronDown className="text-xs" /> : 
              <FaChevronRight className="text-xs" />
            }
          </button>
        ) : hasSubfolders === false ? (
          <span className="w-5 h-5 flex-shrink-0"></span> // Espaciador para alineación
        ) : (
          // Mientras verificamos, mostrar un indicador sutil
          <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            <div className="w-1 h-1 bg-gray-500 rounded-full animate-pulse"></div>
          </div>
        )}
        
        <button
          onClick={() => {
            console.log(`📁 Navegando desde sidebar: "${folder}"`);
            
            // Construir ruta absoluta
            let navigationPath;
            if (!path || path === '') {
              // Si estamos en la raíz del sidebar, usar solo el nombre de la carpeta + '/'
              navigationPath = folder + '/';
            } else {
              // Si estamos en una subcarpeta del sidebar, construir el path completo
              const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;
              navigationPath = `${cleanPath}/${folder}/`;
            }
            
            console.log(`🎯 Path absoluto de navegación: "${navigationPath}"`);
            onFolderOpen && onFolderOpen(navigationPath);
          }}
          className="flex-1 flex items-center space-x-2 px-2 py-1 rounded-lg text-left text-sidebar-text hover:bg-gray-700 hover:text-white transition-colors min-w-0"
        >
          {expanded ? 
            <FaFolderOpen className="text-lg text-blue-400 flex-shrink-0" /> : 
            <FaFolder className="text-lg text-blue-400 flex-shrink-0" />
          }
          <span className="truncate text-sm">{folder}</span>
        </button>
      </div>
      
      {expanded && (
        <div className="ml-4 mt-1 pl-3 border-l border-gray-600 space-y-1">
          {loading ? (
            <div className="flex items-center space-x-2 px-2 py-1 text-xs text-gray-400">
              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Cargando subcarpetas...</span>
            </div>
          ) : subfolders.length > 0 ? (
            subfolders.map((subfolder, idx) => (
              <FolderItem
                key={`${folderPath}-${subfolder}`}
                folder={subfolder}
                path={folderPath}
                onFolderOpen={onFolderOpen}
                currentPath={currentPath}
              />
            ))
          ) : (
            <div className="px-2 py-1 text-xs text-gray-500 italic">
              Sin subcarpetas
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ 
  selectedType, 
  onTypeChange, 
  stats,
  onCreateFolder,
  onUploadFiles,
  onFolderOpen,
  currentPath,
  tags = [],
  tagsLoading = false,
  selectedTag = null,
  onTagSelect,
  onClearTag,
  selectedFolderTag = null,
  onFolderTagSelect,
  onClearFolderTag,
  refreshFolderTags = 0,
  onManageTempFolder = null,
  refreshTempFolders = 0
}) => {
  const [myDriveExpanded, setMyDriveExpanded] = useState(true);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [predefinedTagsLoading, setPredefinedTagsLoading] = useState(false);
  const [predefinedFolderTags, setPredefinedFolderTags] = useState([]);
  const [predefinedFolderTagsLoading, setPredefinedFolderTagsLoading] = useState(false);
  const [predefinedFolderTagsCategory2, setPredefinedFolderTagsCategory2] = useState([]);
  const [predefinedFolderTagsCategory2Loading, setPredefinedFolderTagsCategory2Loading] = useState(false);
  
  // Estados para controlar secciones desplegables de tags
  const [tagsCarpetasExpanded, setTagsCarpetasExpanded] = useState(false);
  const [tagsArchivosExpanded, setTagsArchivosExpanded] = useState(false);
  const [tagsTecweekExpanded, setTagsTecweekExpanded] = useState(false);

  const mainMenuItems = [
    { id: 'all', label: 'Página principal', icon: FaHome, count: stats?.totalFiles },
  ];

  const actions = [
    { id: 'upload', label: 'Subir Archivos', icon: FaPlus, action: onUploadFiles },
    { id: 'new-folder', label: 'Nueva Carpeta', icon: FaFolder, action: onCreateFolder },
  ];

  // Cargar las carpetas raíz cuando se monte el componente
  useEffect(() => {
    const loadRootFolders = async () => {
      setLoading(true);
      try {
        console.log('📁 Sidebar: Cargando carpetas raíz...');
        // Usar la nueva función específica para carpetas raíz
        const rootFolders = await fileService.getRootFolders();
        console.log(`📁 Sidebar: ${rootFolders.length} carpetas raíz cargadas:`, rootFolders.join(', '));
        setFolders(rootFolders);
      } catch (err) {
        console.error('❌ Sidebar: Error cargando carpetas raíz:', err);
        // Fallback: intentar extraer carpetas del listado completo
        try {
          console.log('🔄 Sidebar: Intentando fallback...');
          const result = await fileService.getFiles('');
          const files = result.data || [];
          const rootFolders = extractRootFolders(files);
          console.log(`📁 Sidebar: ${rootFolders.length} carpetas extraídas via fallback:`, rootFolders.join(', '));
          setFolders(rootFolders);
        } catch (fallbackErr) {
          console.error('❌ Sidebar: Error en fallback:', fallbackErr);
          setFolders([]);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadRootFolders();
  }, []);

  // Función para normalizar tags (igual que en el backend)
  const normalizeTag = (tag) => {
    if (!tag || typeof tag !== 'string') return '';
    return tag.toLowerCase().trim().replace(/\s+/g, '-');
  };

  // Función para capitalizar la primera letra de cada palabra
  const capitalizeTag = (tag) => {
    if (!tag || typeof tag !== 'string') return tag;
    // Si el tag tiene guiones, capitalizar cada palabra separada por guión
    if (tag.includes('-')) {
      return tag
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    // Si no tiene guiones, capitalizar solo la primera letra
    return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
  };

  // Cargar tags predefinidos cuando se monte el componente
  useEffect(() => {
    const loadPredefinedTags = async () => {
      setPredefinedTagsLoading(true);
      try {
        console.log('🏷️ Sidebar: Cargando tags predefinidos...');
        
        // Primero cargar los tags predefinidos (más rápido)
        const predefinedTagsList = await fileService.getPredefinedTags();
        console.log(`🏷️ Sidebar: ${predefinedTagsList.length} tags predefinidos obtenidos`);
        
        // Intentar cargar el catálogo con conteos (puede fallar o tardar)
        let tagCatalog = [];
        try {
          tagCatalog = await Promise.race([
            fileService.getTagsCatalog(200),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000)) // Timeout de 10 segundos
          ]);
        } catch (catalogError) {
          console.warn('⚠️ Sidebar: No se pudo cargar catálogo de tags (usando sin conteos):', catalogError.message);
          // Continuar sin catálogo
        }
        
        // Crear un mapa de tags normalizados a conteos desde el catálogo
        const tagCountMap = new Map();
        if (Array.isArray(tagCatalog)) {
          tagCatalog.forEach(tagEntry => {
            const normalized = normalizeTag(tagEntry.tag);
            tagCountMap.set(normalized, tagEntry.count || 0);
          });
        }
        
        // Convertir a formato que espera el Sidebar: {tag, count}
        const tagsWithCount = predefinedTagsList.map(tag => {
          const normalized = normalizeTag(tag);
          const count = tagCountMap.get(normalized) || 0;
          return {
            tag: tag,
            count: count
          };
        });
        
        console.log(`🏷️ Sidebar: ${tagsWithCount.length} tags predefinidos cargados con conteos`);
        setPredefinedTags(tagsWithCount);
      } catch (error) {
        console.error('❌ Sidebar: Error cargando tags predefinidos:', error);
        // Si falla todo, cargar solo los tags predefinidos sin conteo
        try {
          const tags = await fileService.getPredefinedTags();
          setPredefinedTags(tags.map(tag => ({ tag, count: 0 })));
        } catch (fallbackError) {
          console.error('❌ Sidebar: Error en fallback:', fallbackError);
          setPredefinedTags([]);
        }
      } finally {
        setPredefinedTagsLoading(false);
      }
    };
    
    loadPredefinedTags();
  }, []);

  // Función para cargar tags predefinidos de carpetas (expuesta para recargar)
  const loadPredefinedFolderTags = useCallback(async () => {
    setPredefinedFolderTagsLoading(true);
    setPredefinedFolderTagsCategory2Loading(true);
    try {
      console.log('🏷️ Sidebar: Cargando tags predefinidos de carpetas...');
      
      // Primero cargar los tags predefinidos (más rápido)
      const response = await fileService.getPredefinedFolderTags();
      console.log('🏷️ Sidebar: Respuesta completa del backend:', response);
      
      const predefinedTagsList = response.tags || response || [];
      const category1Tags = response.category1 || [];
      const category2Tags = response.category2 || [];
      
      console.log(`🏷️ Sidebar: ${predefinedTagsList.length} tags predefinidos de carpetas obtenidos`);
      console.log(`🏷️ Sidebar: Categoría 1: ${category1Tags.length} tags, Categoría 2: ${category2Tags.length} tags`);
      
      // Intentar cargar el catálogo con conteos (puede fallar o tardar)
      let tagCatalog = [];
      try {
        tagCatalog = await Promise.race([
          fileService.getFolderTagsCatalog(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 60000)) // Timeout de 60 segundos
        ]);
      } catch (catalogError) {
        console.warn('⚠️ Sidebar: No se pudo cargar catálogo de tags de carpetas (usando sin conteos):', catalogError.message);
        // Continuar sin catálogo
      }
      
      // Crear un mapa de tags normalizados a conteos desde el catálogo
      const tagCountMap = new Map();
      if (Array.isArray(tagCatalog)) {
        tagCatalog.forEach(tagEntry => {
          const normalized = normalizeTag(tagEntry.tag);
          tagCountMap.set(normalized, tagEntry.count || 0);
        });
      }
      
      // Convertir a formato que espera el Sidebar: {tag, count}
      const tagsWithCount = category1Tags.map(tag => {
        const normalized = normalizeTag(tag);
        const count = tagCountMap.get(normalized) || 0;
        return {
          tag: tag,
          count: count
        };
      });
      
      const tagsWithCountCategory2 = category2Tags.map(tag => {
        const normalized = normalizeTag(tag);
        const count = tagCountMap.get(normalized) || 0;
        return {
          tag: tag,
          count: count
        };
      });
      
      console.log(`🏷️ Sidebar: ${tagsWithCount.length} tags categoría 1 y ${tagsWithCountCategory2.length} tags categoría 2 cargados con conteos`);
      setPredefinedFolderTags(tagsWithCount);
      setPredefinedFolderTagsCategory2(tagsWithCountCategory2);
    } catch (error) {
      console.error('❌ Sidebar: Error cargando tags predefinidos de carpetas:', error);
      // Si falla todo, cargar solo los tags predefinidos sin conteo
      try {
        const response = await fileService.getPredefinedFolderTags();
        const category1Tags = response.category1 || [];
        const category2Tags = response.category2 || [];
        setPredefinedFolderTags(category1Tags.map(tag => ({ tag, count: 0 })));
        setPredefinedFolderTagsCategory2(category2Tags.map(tag => ({ tag, count: 0 })));
      } catch (fallbackError) {
        console.error('❌ Sidebar: Error en fallback de tags de carpetas:', fallbackError);
        setPredefinedFolderTags([]);
        setPredefinedFolderTagsCategory2([]);
      }
    } finally {
      setPredefinedFolderTagsLoading(false);
      setPredefinedFolderTagsCategory2Loading(false);
    }
  }, []);

  // Cargar tags predefinidos de carpetas cuando se monte el componente o cuando cambie refreshFolderTags
  useEffect(() => {
    loadPredefinedFolderTags();
  }, [loadPredefinedFolderTags, refreshFolderTags]);

  // Extraer carpetas raíz del listado de archivos
  const extractRootFolders = (files) => {
    const foldersSet = new Set();
    
    // Validar que files sea un array
    if (!Array.isArray(files)) {
      console.warn('extractRootFolders: files no es un array:', files);
      return [];
    }
    
    files.forEach(file => {
      const path = file.Key || file.name || '';
      if (path.includes('/')) {
        // Obtener solo el primer nivel
        const rootFolder = path.split('/')[0];
        if (rootFolder) {
          foldersSet.add(rootFolder);
        }
      }
    });
    
    return Array.from(foldersSet).sort();
  };

  return (
    <div className="w-64 bg-sidebar-bg text-sidebar-text h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-r border-gray-700">
       <img src="/images/logo_gcba_azul.jpeg" alt="logo_gcba_azul" style={{width: '45%', height: 'auto'}} />
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-auto px-4 border-r border-gray-700">
        {/* Página Principal */}
        <div className="space-y-2">
          {mainMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                onTypeChange(item.id);
                if (item.id === 'all' && typeof onFolderOpen === 'function') {
                  onFolderOpen('');
                }
              }}
              className={`
                w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors
                ${selectedType === item.id 
                  ? ' text-white' 
                  : 'text-sidebar-text hover:bg-gray-700 hover:text-white'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                <item.icon className="text-lg" />
                <span className="font-medium">{item.label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Mi Unidad */}
        <div className="mt-6">
          <button
            onClick={() => setMyDriveExpanded(!myDriveExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-left text-sidebar-text hover:bg-gray-700 hover:text-white transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FaHdd className="text-lg" />
              <span className="font-medium">Mi unidad</span>
            </div>
            {myDriveExpanded ? (
              <FaChevronDown className="text-sm" />
            ) : (
              <FaChevronRight className="text-sm" />
            )}
          </button>

          {/* Listado dinámico de carpetas con jerarquía */}
          {myDriveExpanded && (
            <div className="ml-4 mt-2 space-y-1 max-h-[40vh] overflow-y-auto">
              {loading ? (
                <div className="flex items-center space-x-2 px-2 py-1 text-sm text-gray-400">
                  <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Cargando carpetas...</span>
                </div>
              ) : folders.length > 0 ? (
                folders.map((folder, index) => (
                  <FolderItem 
                    key={`root-${folder}`}
                    folder={folder}
                    path=""
                    onFolderOpen={onFolderOpen}
                    currentPath={currentPath}
                  />
                ))
              ) : (
                <div className="px-2 py-1 text-sm text-gray-400">
                  No hay carpetas
                </div>
              )}
            </div>
          )}
        </div>

        {/* Carpetas Temporales Section */}
        <TempFoldersList
          onSelectFolder={onFolderOpen}
          onManageFolder={onManageTempFolder}
          refreshTrigger={refreshTempFolders}
        />

          {/* Tags Carpetas Section */}
          <div className="mt-8">
          <div className="px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => setTagsCarpetasExpanded(!tagsCarpetasExpanded)}
              className="flex items-center space-x-2 text-sidebar-text hover:text-white transition-colors"
            >
              <span className="font-medium">Tags Carpetas</span>
              {tagsCarpetasExpanded ? (
                <FaChevronDown className="text-sm" />
              ) : (
                <FaChevronRight className="text-sm" />
              )}
            </button>
            {selectedFolderTag && (
              <button
                onClick={onClearFolderTag}
                className="text-xs text-yellow-400 hover:text-yellow-200 transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>

          {tagsCarpetasExpanded && (
            <div className="px-4 flex flex-wrap gap-2">
            {predefinedFolderTagsLoading ? (
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando tags...</span>
              </div>
            ) : predefinedFolderTags.length > 0 ? (
              predefinedFolderTags.map((tagEntry) => {
                const normalizedSelected = selectedFolderTag ? normalizeTag(selectedFolderTag) : null;
                const normalizedTagValue = normalizeTag(tagEntry.tag);
                const isSelected = normalizedSelected === normalizedTagValue;
                const displayTag = capitalizeTag(tagEntry.tag);
                
                return (
                  <button
                    key={tagEntry.tag}
                    onClick={() => {
                      // Normalizar el tag antes de pasarlo
                      const normalized = normalizeTag(tagEntry.tag);
                      onFolderTagSelect && onFolderTagSelect(normalized);
                    }}
                    className={`
                      inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-xs font-medium border
                      transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:ring-offset-2 focus:ring-offset-gray-900
                      ${isSelected
                        ? 'bg-yellow-600 text-white border-yellow-500 shadow-lg shadow-yellow-500/25'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200 hover:text-yellow-900'}
                    `}
                  >
                    <span className="truncate max-w-[120px]">
                      {displayTag}
                      <span className={`ml-1 ${isSelected ? 'text-yellow-200' : 'text-yellow-600'}`}>
                        [{tagEntry.count || 0}]
                      </span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="text-xs text-gray-500">
                No hay tags disponibles.
              </div>
            )}
            </div>
          )}
        </div>

          {/* Tags Archivos Section */}
          <div className="mt-8">
          <div className="px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => setTagsArchivosExpanded(!tagsArchivosExpanded)}
              className="flex items-center space-x-2 text-sidebar-text hover:text-white transition-colors"
            >
              <span className="font-medium">Tags Archivos</span>
              {tagsArchivosExpanded ? (
                <FaChevronDown className="text-sm" />
              ) : (
                <FaChevronRight className="text-sm" />
              )}
            </button>
            {selectedTag && (
              <button
                onClick={onClearTag}
                className="text-xs text-blue-400 hover:text-blue-200 transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>

          {tagsArchivosExpanded && (
            <div className="px-4 flex flex-wrap gap-2">
            {predefinedTagsLoading ? (
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando tags...</span>
              </div>
            ) : predefinedTags.length > 0 ? (
              predefinedTags.map((tagEntry) => {
                const normalizedSelected = selectedTag ? normalizeTag(selectedTag) : null;
                const normalizedTagValue = normalizeTag(tagEntry.tag);
                const isSelected = normalizedSelected === normalizedTagValue;
                const displayTag = capitalizeTag(tagEntry.tag);
                
                return (
                  <button
                    key={tagEntry.tag}
                    onClick={() => {
                      // Normalizar el tag antes de pasarlo
                      const normalized = normalizeTag(tagEntry.tag);
                      onTagSelect && onTagSelect(normalized);
                    }}
                    className={`
                      inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border
                      transition-colors focus:outline-none
                      ${isSelected
                        ? 'bg-blue-600 text-white border-blue-500'
                        : 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 hover:text-blue-900'}
                    `}
                  >
                    <span className="truncate">
                      {displayTag}
                      <span className={`ml-1 ${isSelected ? 'text-blue-100' : 'text-blue-600'}`}>
                        [{tagEntry.count || 0}]
                      </span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="text-xs text-gray-500">
                No hay tags disponibles.
              </div>
            )}
            </div>
          )}
        </div>

          {/* Tags Carpetas Categoría 2 (Verde) Section */}
          <div className="mt-8">
          <div className="px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => setTagsTecweekExpanded(!tagsTecweekExpanded)}
              className="flex items-center space-x-2 text-sidebar-text hover:text-white transition-colors"
            >
              <span className="font-medium">Tags Tecweek</span>
              {tagsTecweekExpanded ? (
                <FaChevronDown className="text-sm" />
              ) : (
                <FaChevronRight className="text-sm" />
              )}
            </button>
            {selectedFolderTag && (
              <button
                onClick={onClearFolderTag}
                className="text-xs text-green-400 hover:text-green-200 transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>

          {tagsTecweekExpanded && (
            <div className="px-4 flex flex-wrap gap-2">
            {predefinedFolderTagsCategory2Loading ? (
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando tags...</span>
              </div>
            ) : predefinedFolderTagsCategory2.length > 0 ? (
              predefinedFolderTagsCategory2.map((tagEntry) => {
                const normalizedSelected = selectedFolderTag ? normalizeTag(selectedFolderTag) : null;
                const normalizedTagValue = normalizeTag(tagEntry.tag);
                const isSelected = normalizedSelected === normalizedTagValue;
                const displayTag = capitalizeTag(tagEntry.tag);
                
                return (
                  <button
                    key={tagEntry.tag}
                    onClick={() => {
                      // Normalizar el tag antes de pasarlo
                      const normalized = normalizeTag(tagEntry.tag);
                      onFolderTagSelect && onFolderTagSelect(normalized);
                    }}
                    className={`
                      inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-xs font-medium border
                      transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:ring-offset-2 focus:ring-offset-gray-900
                      ${isSelected
                        ? 'bg-green-600 text-white border-green-500 shadow-lg shadow-green-500/25'
                        : 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200 hover:text-green-900'}
                    `}
                  >
                    <span className="truncate max-w-[120px]">
                      {displayTag}
                      <span className={`ml-1 ${isSelected ? 'text-green-200' : 'text-green-600'}`}>
                        [{tagEntry.count || 0}]
                      </span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="text-xs text-gray-500">
                No hay tags disponibles.
              </div>
            )}
            </div>
          )}
        </div>

        {/* Actions Section */}
        <div className="mt-8">
          <div className="px-4 py-2">
            <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
              Acciones
            </span>
          </div>
          
          <div className="space-y-1 mt-2">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={action.action}
                className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left text-sidebar-text hover:bg-gray-700 hover:text-white transition-colors"
              >
                <action.icon className="text-lg" />
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>

      

        {/* Estadísticas Section */}
        {stats && (
          <div className="mt-8 mb-4">
            <div className="px-4 py-2">
              <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
                Estadísticas
              </span>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4 mt-2">
              <div className="flex items-center space-x-2 mb-3">
                <FaChartBar className="text-blue-400" />
                <span className="text-sm font-semibold text-gray-300">
                  Bucket Total
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Total de archivos:</span>
                  <span className="text-white">{stats.totalFiles?.toLocaleString() || 0}</span>
                </div>
                
                <div className="flex justify-between text-gray-400">
                  <span>Tamaño total:</span>
                  <span className="text-white">{stats.totalSizeFormatted || stats.totalSize || '0 B'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <div className="text-xs text-gray-500 text-center">
          GCBA S3 File Manager
        </div>
      </div>
    </div>
  );
};

export default Sidebar;